import * as React from "react";
import {
	SingleScrollContainer,
	NormalContainer,
	GridOneBaseBox,
	LeftIconArrow,
	RightsIconArrow,
	ProductCardWrap,
} from "./styles";
import { Size, TProductElement } from "../../../../services/product";
import { ProductCard } from "../../NormalProducts/ProductCard";
import { getCellWidthByGrid as getProductCardPosterWidth } from "../../../utils/size";
import { ModuleLayoutStyle } from "../../../../services/playground";
import { WebSlideProductsProps } from "../index";

const _animationDuration = 1000;

export type SwipeProductProps = {
	products: TProductElement[];
	gridWidth: number;
	posterRatio: number;
	gridCol: number;
	gridGap: number;
	id: string;
	layoutStyle: ModuleLayoutStyle;
	placeholderBg: string;
	handWheel?: Size;
	onClick?: (pos: string, pid: string) => void;
	onClickBack?: () => void;
	onClickNext?: () => void;
	poolId: string;
	originPriceBr?: Boolean;
} & Pick<WebSlideProductsProps, "scoreOptions" | "smartImageProps">;

interface SwipeProductState {
	cIndex: number;
	animationDuration: number;
	needAdd: number;
}

class SwipeProduct extends React.Component<SwipeProductProps, SwipeProductState> {
	static defaultProps = {
		gridGap: 12,
	};
	canClick: boolean = true;
	autoSwipe: boolean = false;
	countDown: any = 0;
	productCardWidth: number = getProductCardPosterWidth({
		contentWidth: this.props.gridWidth,
		gridCol: this.props.gridCol,
		gridGap: this.props.gridGap,
	});
	productsOnOnePage: number = this.props.gridCol * 1;

	state: SwipeProductState = {
		cIndex: 0,
		animationDuration: 0,
		needAdd: Math.ceil((this.productsOnOnePage * 3) / 2),
	};

	onBack = () => {
		if (!this.canClick || this.state.cIndex > 20) {
			return;
		}
		this.canClick = false;
		this.setState((state) => ({
			cIndex: state.cIndex - this.productsOnOnePage,
			animationDuration: _animationDuration,
		}));
		this.props.onClickBack && this.props.onClickBack();
	};

	onNext = () => {
		if (!this.canClick) {
			return;
		}
		this.canClick = false;
		this.setState((state) => ({
			cIndex: state.cIndex + this.productsOnOnePage,
			animationDuration: _animationDuration,
		}));

		this.props.onClickNext && this.props.onClickNext();
	};

	onTransitionEnd = () => {
		const { products } = this.props;
		const { cIndex, needAdd } = this.state;
		if (cIndex + needAdd > products.length + this.productsOnOnePage) {
			this.setState({
				cIndex: cIndex - this.getOffset() - (products.length % this.productsOnOnePage),
				animationDuration: 0,
			});
		}
		if (cIndex - this.productsOnOnePage < -needAdd) {
			this.setState({
				cIndex: cIndex + this.getOffset() + (products.length % this.productsOnOnePage),
				animationDuration: 0,
			});
		}
		this.canClick = true;
	};

	getOffset = () => {
		const { products } = this.props;
		return Math.floor(products.length / this.productsOnOnePage) * this.productsOnOnePage;
	};

	getNodeCount() {
		const { products } = this.props;
		const { needAdd } = this.state;
		return products.length + needAdd * 2;
	}

	getSingleStyle = () => {
		const { cIndex, animationDuration, needAdd } = this.state;
		const style: React.CSSProperties = {};
		style.width = `${this.getNodeCount()}00%`;

		const wrapperOffsetLeft =
			-1 * (cIndex + needAdd) * (this.productCardWidth + this.props.gridGap);
		style.transform = `translate3d(${wrapperOffsetLeft}px,0,0)`;
		style.transitionDuration = `${animationDuration}ms`;
		style.WebkitTransform = style.transform;
		style.transitionTimingFunction = "ease-out";
		return style;
	};

	renderCard = (product: TProductElement, index: number) => {
		const {
			gridWidth: width,
			posterRatio,
			gridCol,
			gridGap,
			layoutStyle,
			placeholderBg,
			scoreOptions,
			smartImageProps,
			onClick,
			poolId,
			originPriceBr,
		} = this.props;
		const productCardWidth = +((width - gridGap * (gridCol - 1)) / gridCol).toFixed(1);
		return (
			<ProductCardWrap id={`${poolId}${product.gpid}`}>
				<ProductCard
					key={index}
					product={{ ...product }}
					width={`${productCardWidth}px`}
					posterRatio={posterRatio}
					gridCol={gridCol}
					layoutStyle={layoutStyle}
					collectOptions={scoreOptions}
					smartImageProps={smartImageProps}
					onClick={() => onClick && onClick((index + 1).toString(), product.gpid)}
					placeholderBg={placeholderBg}
					originPriceBr={originPriceBr}
				/>
			</ProductCardWrap>
		);
	};
	renderProductContent = () => {
		const { products } = this.props;

		const { needAdd } = this.state;
		if (products.length === 5) {
			return (
				<React.Fragment>
					{products
						.slice(-1)
						.map((product, index) => this.renderCard(product, index - 6))}
					{products.map((product, index) => this.renderCard(product, index - 5))}
					{products.map((product, index) => this.renderCard(product, index))}
					{products.map((product, index) =>
						this.renderCard(product, index + products.length),
					)}
					{products
						.slice(0, 1)
						.map((product, index) =>
							this.renderCard(product, index + products.length + 1),
						)}
				</React.Fragment>
			);
		}
		return (
			<React.Fragment>
				{products.slice(-needAdd).map((product, index) => this.renderCard(product, index))}
				{products.map((product, index) => this.renderCard(product, index))}
				{products
					.slice(0, needAdd)
					.map((product, index) => this.renderCard(product, index))}
			</React.Fragment>
		);
	};

	render() {
		const { products, handWheel } = this.props;
		if (products.length <= this.productsOnOnePage) {
			return (
				<SingleScrollContainer
					gridWidth={this.productCardWidth}
					gridGap={this.props.gridGap}
				>
					{products.map((product, index) => this.renderCard(product, index))}
				</SingleScrollContainer>
			);
		}

		return (
			<NormalContainer
				gridWidth={this.props.gridWidth}
				onMouseEnter={() => (this.autoSwipe = false)}
				onMouseLeave={() => (this.autoSwipe = true)}
			>
				<LeftIconArrow {...handWheel} onClick={this.onBack} />

				<GridOneBaseBox
					gridWidth={this.productCardWidth}
					gridGap={this.props.gridGap}
					style={this.getSingleStyle()}
					onTransitionEnd={this.onTransitionEnd}
				>
					{this.renderProductContent()}
				</GridOneBaseBox>

				<RightsIconArrow {...handWheel} onClick={this.onNext} />
			</NormalContainer>
		);
	}
}

export default SwipeProduct;
